import { format, fromUnixTime } from 'date-fns';
import React, { useMemo } from 'react';
import { Col, Row } from 'react-styled-flexboxgrid';
import BackgroundBox from '../../../../../../components/BackgroundBox';
import Box from '../../../../../../components/Box';
import Button from '../../../../../../components/Button';
import ButtonGroup from '../../../../../../components/ButtonGroup';
import Divider from '../../../../../../components/Divider';
import List from '../../../../../../components/List';
import ListItem from '../../../../../../components/ListItem';
import Table from '../../../../../../components/Table';
import Typography from '../../../../../../components/Typography';
import useTheme from '../../../../../../hooks/useTheme';
import { makeStyles } from '../../../../../../utils/styles';
import Icon from '../../../../../../components/Icon';
import { generateTablePdf, parseDataToPDFBody } from '../../../../../../utils/pdf';
import { calcAverage, calcSum, getLabelTime } from '../../../../../../utils/helpers';
import Paper from '../../../../../../components/Paper';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

const useStyles = makeStyles(css => ({
    listPaper: css`
        padding: 0 20px;
        height: 100%;
    `,
    box: css`
        height: 100%;
    `,
    divider: css`
        margin: 20px 0;
    `,
    boxWrapper: css`
        padding: 0px 20px 20px 20px;
    `,
    dividerTop: css`
        margin-bottom: 20px;
    `
}));

const RowSubComponent = ({ row, onClickDelete }) => {
    const { t } = useTranslation()
    const theme = useTheme();
    const styles = useStyles();
    const { currency } = useSelector((state) => state.currency);

    // Memoize data
    const report = useMemo(
        () => ({
            ...row.original
        }),
        [row]
    );

    const data = useMemo(
        () => report?.job_reports || [],
        [report]
    );

    const handleClickDownload = () => {
        const parsedData = parseDataToPDFBody(data, (report) => [
            format(fromUnixTime(report?.date), 'dd.MM.yyyy, HH:mm'),
            report.name,
            report.position,
            report.places_amount,
            report.applications_amount,
            report.absences,
            report.hours,
            report.work_hours,
            report.raings,
            `${report.cost} ${currency}`
        ]);

        generateTablePdf({
            title: t("Raport {{NAZWA}}", { NAZWA: report?.name !== null ? report?.name : t('bez nazwy') }),
            subtitle: t("Raport od {{startDate}} do {{endDate}}", { startDate: report?.time_start_from ? format(fromUnixTime(report?.time_start_from), 'dd.MM.yyyy') : t('brak'), endDate: report?.time_start_to ? format(fromUnixTime(report?.time_start_to), 'dd.MM.yyyy') : 'brak' }),
            rows: [
                {
                    title: t(`Całkowity koszt hotelu`),
                    text: `${report?.places_cost} ${currency}`
                },
                {
                    title: t("Średni czas od dodania zlecenia do kompletacji pracowników"),
                    text: getLabelTime(report?.time_from_creation_to_completion)
                },
                {
                    title: t(`Liczba roboczogodzin`),
                    text: report?.work_hours_amount
                }
            ],
            tableTitle: 'Lista',
            table: {
                head: [
                    [
                        t('Data świadczenia pracy'),
                        t('Nazwa zlecenia'),
                        t('Stanowisko'),
                        t('Liczba miejsc'),
                        t('Liczba zgłoszeń'),
                        t('Liczba nieobecności'),
                        t('Godziny'),
                        t('Rob. godz.'),
                        t('Śr. ocena'),
                        t('Koszt brutto')
                    ]
                ],
                body: parsedData,
                foot: parsedData && parsedData.length > 0 ? [
                    [
                        '',
                        '',
                        '',
                        calcSum(parsedData, (row) => row[3]),
                        calcSum(parsedData, (row) => row[4]),
                        calcSum(parsedData, (row) => row[5]),
                        calcSum(parsedData, (row) => row[6]),
                        calcSum(parsedData, (row) => row[7]),
                        calcAverage(parsedData, (row) => row[8]),
                        `${calcSum(parsedData, (row) => parseInt(row[9].replace(` ${currency}`, '')))} ${currency}`
                    ]
                ] : [],
            },
            filename: `raport_${report?.name !== null ? report?.name : 'bez_nazwy'}_${report.date_place?.date && format(fromUnixTime(report.date_place?.date), 'dd.MM.yyyy')}`
        });
    }

    const columns = useMemo(
        () => [
            {
                Header: t('Data świadczenia pracy'),
                accessor: 'date',
                Cell: ({ cell: { value } }) => {
                    return value ? format(fromUnixTime(value), 'dd.MM.yyyy, HH:mm') : "";
                }
            },
            {
                Header: t('Nazwa zlecenia'),
                accessor: 'name'
            },
            {
                Header: t('Stanowisko'),
                accessor: 'position'
            },
            {
                Header: t('Liczba miejsc'),
                accessor: 'places_amount',
                Footer: info => {
                    return useMemo(
                        () => calcSum(info.rows, (row) => row.values?.places_amount),
                        [info.rows]
                    );
                }
            },
            {
                Header: t('Liczba zgłoszeń'),
                accessor: 'applications_amount',
                Footer: info => {
                    return useMemo(
                        () => calcSum(info.rows, (row) => row.values?.applications_amount),
                        [info.rows]
                    );
                }
            },
            {
                Header: t('Liczba nieobecności'),
                accessor: 'absences',
                Footer: info => {
                    return useMemo(
                        () => calcSum(info.rows, (row) => row.values?.absences),
                        [info.rows]
                    );
                }
            },
            {
                Header: t('Godziny'),
                accessor: 'hours',
                Footer: info => {
                    return useMemo(
                        () => calcSum(info.rows, (row) => row.values?.hours),
                        [info.rows]
                    );
                }
            },
            {
                Header: t('Rob. godz.'),
                accessor: 'work_hours',
                Footer: info => {
                    return useMemo(
                        () => calcSum(info.rows, (row) => row.values?.work_hours),
                        [info.rows]
                    );
                }
            },
            {
                Header: t('Śr. ocena'),
                accessor: 'raings',
                Footer: info => {
                    return useMemo(
                        () => calcAverage(info.rows, (row) => row.values?.raings),
                        [info.rows]
                    );
                }
            },
            {
                Header: t('Koszt brutto'),
                accessor: 'cost',
                Cell: ({ cell: { value } }) => `${value} ${currency}`,
                Footer: info => {
                    return useMemo(
                        () => `${calcSum(info.rows, (row) => row.values?.cost)} ${currency}`,
                        [info.rows]
                    );
                }
            },
        ],
        [t, currency]
    )

    return (
        <Box customStyle={styles.boxWrapper}>
            <Divider customStyle={styles.dividerTop} />
            <Box>
                <Row>
                    <Col xs={3}>
                        <BackgroundBox
                            topText={t("Całkowity koszt hotelu") + ` <br/> ${report?.time_start_from ? format(fromUnixTime(report.time_start_from), 'dd.MM.yyyy') : t('brak')} - ${report?.time_start_to ? format(fromUnixTime(report.time_start_to), 'dd.MM.yyyy') : t('brak')}`}
                            bottomText={`${report?.places_cost} ${currency}`}
                            customStyle={styles.box}

                        />
                    </Col>
                    <Col xs={3}>
                        <BackgroundBox
                            topText={t("Średni czas od dodania zlecenia do kompletacji pracowników")}
                            bottomText={getLabelTime(report?.time_from_creation_to_completion)}
                            customStyle={styles.box}
                            backgroundColor={theme.colors.blue}
                        />
                    </Col>
                    <Col xs={3}>
                        <BackgroundBox
                            topText={t("Liczba roboczogodzin") + ` <br/> ${report?.time_start_from ? format(fromUnixTime(report.time_start_from), 'dd.MM.yyyy') : t('brak')} - ${report?.time_start_to ? format(fromUnixTime(report.time_start_to), 'dd.MM.yyyy') : t('brak')}`}
                            bottomText={report?.work_hours_amount}
                            customStyle={styles.box}
                            backgroundColor={theme.colors.steelBlue}
                        />
                    </Col>
                    <Col xs={3}>
                        <Paper
                            customStyle={styles.listPaper}
                        >
                            <List>
                                <ListItem>
                                    <Typography>{t("Średnia ocena")}</Typography>
                                    <Typography>{report?.average_ranking}</Typography>
                                </ListItem>
                                <ListItem>
                                    <Typography>{t("Liczba nieobecności")}</Typography>
                                    <Typography>{report?.absences_amount}</Typography>
                                </ListItem>
                                <ListItem>
                                    <Typography>{t("Liczba spóźnień")}</Typography>
                                    <Typography>{report?.late_amount}</Typography>
                                </ListItem>
                            </List>
                        </Paper>
                    </Col>
                </Row>
                <Divider customStyle={styles.divider} />
                <Table columns={columns} data={data} />
                <Row end="xs">
                    <Col>
                        <ButtonGroup
                            gutters={10}
                        >
                            <Button
                                variant="text"
                                onClick={onClickDelete(report?.id)}
                                startIcon={<Icon name="close" width={12} height={12} fill={theme.colors.purple} />}
                            >
                                {t("Usuń raport")}
                            </Button>
                            <Button
                                variant="text"
                                onClick={handleClickDownload}
                                startIcon={<Icon name="download" width={12} height={12} fill={theme.colors.purple} />}
                            >
                                {t("Pobierz raport")}
                            </Button>
                        </ButtonGroup>
                    </Col>
                </Row>
            </Box>
        </Box>
    )
};

export default RowSubComponent;
